import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Search, Loader2 } from "lucide-react";
import { CaseDrawer } from "@/components/CaseDrawer";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

export function GlobalSearch() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canManage = ["admin", "ops_manager"].includes(user.role);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openCase, setOpenCase] = useState(null);
  const box = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setResults(null); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onChange = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/workflow/search?q=${encodeURIComponent(v.trim())}`);
        setResults(data.results || []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 300);
  };

  return (
    <div className="relative hidden sm:block" ref={box}>
      <div className="flex items-center gap-2 rounded-full border bg-white px-3 py-1.5">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input value={q} onChange={(e) => onChange(e.target.value)} placeholder={t("search.placeholder")}
          className="w-56 bg-transparent text-sm outline-none" data-testid="global-search-input" />
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>
      {results && (
        <div className="absolute left-0 top-11 z-50 max-h-96 w-96 overflow-y-auto rounded-lg border bg-card p-1 shadow-xl" data-testid="global-search-results">
          {results.length === 0 ? <div className="p-3 text-xs text-muted-foreground">{t("search.no_matches")}</div> :
            results.map((r, i) => (
              <button key={i} onClick={() => { if (r.type === "lead") { setOpenCase(r.lead_id); setResults(null); } }}
                data-testid={`gs-result-${i}`}
                className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-muted">
                <div>
                  <div className="font-medium">{r.client_name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.type} · {r.canonical || "—"} {r.policy_number ? `· ${r.policy_number}` : ""} {r.carrier ? `· ${r.carrier}` : ""}
                  </div>
                </div>
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase">{r.type}</span>
              </button>
            ))}
          <div className="border-t px-3 py-1.5 text-[10px] text-muted-foreground">{t("search.hint")}</div>
        </div>
      )}
      {openCase && <CaseDrawer leadId={openCase} onClose={() => setOpenCase(null)} canManage={canManage} />}
    </div>
  );
}
